import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, initialAllowancesQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function WisconsinForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFullName,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [forYourself, setForYourself] = useState(data.for_yourself || false);
  const [forSpouse, setForSpouse] = useState(data.for_spouse || false);

  const allowancesForm = useRef();
  const allowancesQuestionsWithAnswers = getInitialQuestions(initialAllowancesQuestions);
  const [allowancesQuestions, setAllowancesQuestions] = useState(allowancesQuestionsWithAnswers);

  const [isExempt, setIsExempt] = useState(data.is_exempt || false);

  // to calculate total number of allowances
  const calculateTotalNoAllowances = () => {
    let value = 0;

    if (forYourself) value += 1;
    if (forSpouse) value += 1;

    const { dependents } = allowancesQuestions[0].answer.answer;
    if (/^\d+$/.test(dependents)) value += parseInt(dependents);

    return value;
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemptions from withholding (Line 3)
      </Text>
      <Text mb="10px">
        You may claim exemption from withholding of Wisconsin income tax if you
        had no liability for income tax for last year, and you expect to incur
        no liability for income tax for this year. You may not claim exemption
        if your return shows tax liability before the allowance of any credit for
        income tax withheld. If you are exempt, your employer will not withhold
        Wisconsin income tax from your wages.
      </Text>
      <S.QuestionWrapper>
        <Flex column>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={isExempt}
                checked={isExempt}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExempt(e.target.checked);
                }}
              />
              <Box ml={2} />
              Check this box to claim complete exemption from withholding.
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderAllowances = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemptions
      </Text>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Total number of exemptions:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={calculateTotalNoAllowances()}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Check any boxes that apply to claim an exemption: (Line 1)
          </S.QuestionLabel>
        </Flex>
        <Flex column>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={forYourself}
                  checked={forYourself}
                  disabled={isReviewStep}
                  onChange={(e) => setForYourself(e.target.checked)}
                />
                <Box ml={2} />
                Exemption for yourself
              </Flex>
            </label>
          </Flex>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={forSpouse}
                  checked={forSpouse}
                  disabled={isReviewStep}
                  onChange={(e) => setForSpouse(e.target.checked)}
                />
                <Box ml={2} />
                Exemption for your spouse
              </Flex>
            </label>
          </Flex>
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={allowancesQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowancesQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowancesQuestions(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderExemptionsFromWithholding()}
          {!isExempt && renderAllowances()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let forYourselfTemp = false;
            let forSpouseTemp = false;
            let totalNumAllowancesTemp = '';
            let allowancesData = resetInitialQuestions(initialAllowancesQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            if (!isExempt) {
              forYourselfTemp = forYourself;
              forSpouseTemp = forSpouse;
              totalNumAllowancesTemp = calculateTotalNoAllowances().toString();
              allowancesData = allowancesQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            }
            const allowancesCheckboxData = {
              for_yourself: forYourselfTemp,
              for_spouse: forSpouseTemp,
              total_number_of_allowances: totalNumAllowancesTemp,
            };
            const exemptionData = { is_exempt: isExempt };
            const data = {
              ...stepOneData, ...allowancesCheckboxData, ...allowancesData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderExemptionsFromWithholding(true)}
        {!isExempt && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default WisconsinForm;
